import { useState } from 'react';

import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

import { DEFAULT_WEEK_DAYS, WeekDay } from '../components/WeekDayPicker';

// Semana ISO: lunes → domingo
dayjs.extend(isoWeek);

const MAX_DAYS = 14;

/**
 * Calcula el rango por defecto: semana anterior (lunes) → semana actual (domingo).
 */
function getDefaultRange(): [Dayjs, Dayjs] {
  const start = dayjs().subtract(1, 'week').startOf('isoWeek');
  const end = dayjs().endOf('isoWeek').startOf('day');
  return [start, end];
}

const DEFAULT_RANGE = getDefaultRange();

export interface UseDashboardFiltersReturn {
  dateRange: [Dayjs, Dayjs];
  setDateRange: (range: [Dayjs, Dayjs]) => void;
  selectedCampaignIds: number[];
  setSelectedCampaignIds: (ids: number[]) => void;
  selectedWeekDays: WeekDay[];
  setSelectedWeekDays: (days: WeekDay[]) => void;
  disabledDate: (current: Dayjs, info: { from?: Dayjs }) => boolean;
  isFiltered: boolean;
  filterKey: number;
  onApply: () => void;
  onReset: () => void;
  applyPreset: (range: [Dayjs, Dayjs], weekDays: WeekDay[]) => void;
}

export function useDashboardFilters(
  onApplyCallback?: (
    dateRange: [Dayjs, Dayjs],
    campaignIds: number[],
    weekDays: WeekDay[]
  ) => void
): UseDashboardFiltersReturn {
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(getDefaultRange);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<number[]>([]);
  const [selectedWeekDays, setSelectedWeekDays] =
    useState<WeekDay[]>(DEFAULT_WEEK_DAYS);

  // Refleja si el último filtro APLICADO difiere del default.
  // Solo cambia al hacer Apply o Reset, nunca al tocar los inputs.
  const [isFiltered, setIsFiltered] = useState(false);

  // Incrementa cada vez que se aplica o resetea — permite que hijos
  // reaccionen a un nuevo fetch sin necesidad de levantar su estado.
  const [filterKey, setFilterKey] = useState(0);

  const disabledDate = (current: Dayjs, info: { from?: Dayjs }): boolean => {
    if (info.from) {
      return Math.abs(current.diff(info.from, 'day')) >= MAX_DAYS;
    }
    return false;
  };

  const onApply = () => {
    const defaultRange = DEFAULT_RANGE;
    const filtered =
      selectedCampaignIds.length > 0 ||
      !dateRange[0].isSame(defaultRange[0], 'day') ||
      !dateRange[1].isSame(defaultRange[1], 'day') ||
      selectedWeekDays.length !== DEFAULT_WEEK_DAYS.length ||
      selectedWeekDays.some((d, i) => d !== DEFAULT_WEEK_DAYS[i]);
    setIsFiltered(filtered);
    setFilterKey((k) => k + 1);
    onApplyCallback?.(dateRange, selectedCampaignIds, selectedWeekDays);
  };

  const onReset = () => {
    const fresh = getDefaultRange();
    setDateRange(fresh);
    setSelectedCampaignIds([]);
    setSelectedWeekDays(DEFAULT_WEEK_DAYS);
    setIsFiltered(false);
    setFilterKey((k) => k + 1);
    onApplyCallback?.(fresh, [], DEFAULT_WEEK_DAYS);
  };

  const applyPreset = (range: [Dayjs, Dayjs], weekDays: WeekDay[]) => {
    setDateRange(range);
    setSelectedWeekDays(weekDays);
    setIsFiltered(true);
    setFilterKey((k) => k + 1);
    onApplyCallback?.(range, selectedCampaignIds, weekDays);
  };

  return {
    dateRange,
    setDateRange,
    selectedCampaignIds,
    setSelectedCampaignIds,
    selectedWeekDays,
    setSelectedWeekDays,
    disabledDate,
    isFiltered,
    filterKey,
    onApply,
    onReset,
    applyPreset,
  };
}
